/**
 * Tier/plan definitions for the onboarding flow
 */

import type { Plan, PublishedPlan } from "@headerhawk/core";
import {
  TIER_QUOTAS,
  TRIAL_DAYS as CORE_TRIAL_DAYS,
  catalogPrice,
  planHasTrial,
  type SelfServePlan,
} from "@headerhawk/core/plans";
import catalog from "../../../../../infra/stripe/catalog.json";

export type { Plan };

/**
 * The free trial, in days, as checkout grants it: on every paid plan since
 * task 402, once per customer.
 */
export const TRIAL_DAYS = CORE_TRIAL_DAYS;

/** Whether the tier's checkout starts with the no-card trial. */
export { planHasTrial as tierHasTrial };

export type BillingInterval = "monthly" | "annual";

export interface TierPricing {
  monthly: number;
  annual: number;
}

export interface TierOption {
  /**
   * `PublishedPlan`, not `Plan`: these cards are how a customer picks and buys
   * a plan, and Enterprise has no published price or published limits — its
   * figures in `TIER_QUOTAS` are a contract's floor (task 345).
   */
  id: PublishedPlan;
  name: string;
  price: TierPricing;
  features: {
    sites: number;
    monthlyReports: number;
    retention: number;
    users: number;
  };
  highlights: string[];
  popular?: boolean;
}

/**
 * Calculate annual savings (difference between 12x monthly and annual price)
 */
export function getAnnualSavings(tier: TierOption): number {
  return tier.price.monthly * 12 - tier.price.annual;
}

/**
 * A plan's current prices, from the Stripe catalog Terraform applies
 * (`infra/stripe/catalog.json`), through the rule the marketing site's pricing
 * page uses too (task 402). This app kept a typed copy of every price until
 * then, which a reprice had to find by hand.
 */
function priceFor(plan: SelfServePlan): TierPricing {
  return {
    monthly: catalogPrice(catalog, plan, "monthly"),
    annual: catalogPrice(catalog, plan, "annual"),
  };
}

/**
 * A plan's limits, read from what the code enforces rather than restated
 * (task 278).
 */
function featuresFor(plan: PublishedPlan): TierOption["features"] {
  const quota = TIER_QUOTAS[plan];
  return {
    sites: quota.siteLimit,
    monthlyReports: quota.monthlyReportLimit,
    retention: quota.retentionDays,
    users: quota.userLimit,
  };
}

export const TIER_OPTIONS: TierOption[] = [
  {
    id: "free",
    name: "Free",
    price: { monthly: 0, annual: 0 },
    features: featuresFor("free"),
    highlights: [
      "Try it on a real site",
      "Every browser report type",
      `${TIER_QUOTAS.free.retentionDays}-day data retention`,
    ],
  },
  {
    id: "team",
    name: "Team",
    price: priceFor("team"),
    features: featuresFor("team"),
    highlights: [
      "For rolling out a policy as a team",
      "Slack, webhooks and the script inventory",
      `${TIER_QUOTAS.team.retentionDays}-day retention`,
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: priceFor("professional"),
    features: featuresFor("professional"),
    highlights: [
      "For PCI DSS 6.4.3 and 11.6.1",
      "Payment pages, script authorization and daily header scans",
      "Priority support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: priceFor("business"),
    features: featuresFor("business"),
    highlights: [
      "Everything in Professional, at scale",
      "For agencies and companies with many sites",
      "Dedicated support",
    ],
  },
];
